/*
|--------------------------------------------------------------------------
| Component: TextAreaField.jsx
| Author: Sayed Ahmed
| Description: Form textarea field supporting optional rich text editing
|              (via ReactQuill), validation, and error messages.
|--------------------------------------------------------------------------
*/

import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { quillModules as defaultQuillModules } from "../../../utils/quillConfig";

export default function TextAreaField({
  label,                   // Label text
  name,                    // Field name
  register,                // RHF register method
  required,                // Required validation
  errors,                  // RHF error object
  value,                   // Controlled value (optional)
  onChange,                // Controlled onChange handler (optional)
  watch,                   // RHF watch function
  setValue,                // RHF setValue function
  isRichTextEditor = false,// Whether to use ReactQuill
  quillModules,            // Custom Quill modules (optional)
  quillTheme = "snow",     // Quill theme
  className = "",          // Custom class
  placeholder = "",        // Input placeholder
}) {
  const { t } = useTranslation();
  const usedModules = quillModules || defaultQuillModules;

  // Register field manually if using ReactQuill
  useEffect(() => {
    if (isRichTextEditor && register) {
      register(name, {
        required: required ? t("Field is required") : false,
      });
    }
  }, [register, name, required, isRichTextEditor, t]);

  const watchedValue = watch?.(name);

  return (
    <div className="form-group mb-3">
      {label && (
        <label htmlFor={name}>
          {t(label)} {required && <span className="text-red-500">*</span>}
        </label>
      )}

      {isRichTextEditor ? (
        <ReactQuill
          id={name}
          value={value ?? watchedValue}
          onChange={(val) => {
            onChange?.(val);
            setValue?.(name, val);
          }}
          modules={usedModules}
          theme={quillTheme}
          className={`mt-2 ${className}`}
        />
      ) : (
        <textarea
          id={name}
          className={`form-control ${className}`}
          placeholder={placeholder}
          {...(register
            ? register(name, {
                required: required ? t("Field is required") : false,
              })
            : { name, value, onChange })}
        />
      )}

      {errors?.[name]?.message && (
        <p className="text-red-600 text-sm">{t(errors[name].message)}</p>
      )}
    </div>
  );
}
